import { AsyncButton, loadWrapper } from "aic";
import { useHBC } from "components/hierarchybrowser/HierarchyBrowserContextProvider";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { ADD_HIERARCHY_NODES } from "contexts/hierarchy/interface";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { eAccessRights, eHierarchy } from "functions/api/enums";
import { copyReport, CopyReportParams } from "functions/api/hierarchy/copyReport";
import { IRequestStatus } from "functions/api/interface";
import { useState } from "react";

export interface CopyReportModalOkProps {
  onClick: () => void;
}

/**
 * OK button for the copy report modal: performs the copy and navigates to the new report.
 *
 * @param props - Destructured parameter object
 * @param props.onClick - Callback invoked after the modal action completes.
 */
export const CopyReportModalOk = ({ onClick }: CopyReportModalOkProps) => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const { state, arRequest } = useHBC();
  const { navigate } = useUnsavedChanges("copy-report-modal-ok");

  const [copyRequest, setCopyRequest] = useState<IRequestStatus<CopyReportParams>>({
    requesting: false,
    error: false,
  });

  if (!state || !hierarchyContext || !hierarchyContext.hierarchy) return null;

  /**
   * Calls the API to copy the report and handles the response.
   */
  const callCopyReport = async () => {
    /* istanbul ignore else */
    if (state.currentHierarchyType === eHierarchy.StudyDirectory && state.newReportName) {
      const request = await loadWrapper(
        copyReport,
        {
          reportId: hierarchyContext.hierarchy!.id,
          hierarchyId: state.currentHierarchy,
          newReportName: state.newReportName,
          parentTag:
            hierarchyContext.hierarchyNodes.find((h) => h.id === state.currentHierarchy)
              ?.parentTag ??
            // istanbul ignore next
            "",
          description: state?.newReportDescription ?? "",
          reportType: state?.newReportType ?? "",
        },
        copyRequest,
        setCopyRequest,
        appSettings.backEndUrl,
      );
      /* istanbul ignore else */
      if (request?.success) {
        hierarchyContext.dispatch({
          operation: ADD_HIERARCHY_NODES,
          hierarchyNodes: [request.data],
        });
        navigate(`/R/${request.data.id}`);
        onClick();
      }
    }
  };

  return (
    <AsyncButton
      id="copyreportmodal-save"
      style={{ minWidth: "120px", minHeight: "32px", position: "relative" }}
      variant={
        arRequest.error || copyRequest.error
          ? "danger"
          : state.currentAccessRights?.includes(eAccessRights.WriteToTlfItemsInHierarchy)
            ? "primary"
            : "secondary"
      }
      disabled={
        !state.newReportValid ||
        !state.currentHierarchy ||
        arRequest.error ||
        arRequest.requesting ||
        !state.currentAccessRights ||
        !state.currentAccessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ||
        copyRequest.requesting ||
        state.currentHierarchyType !== eHierarchy.StudyDirectory
      }
      onClick={() => {
        callCopyReport();
      }}
      title={arRequest.errorText ?? copyRequest.errorText}
      requestStatus={[
        { ...arRequest, errorText: "Error getting rights" },
        { ...copyRequest, errorText: "Error copying, please refresh the page" },
      ]}
      pauseCoverText={arRequest.requesting ? "Loading rights..." : "Copying..."}
    >
      {state.currentAccessRights &&
      state.currentAccessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy)
        ? "Copy"
        : "No write access to this level"}
    </AsyncButton>
  );
};

CopyReportModalOk.displayName = "CopyReportModalOk";
